import { Table as BaseTable, Box, LoadingOverlay, Pagination, ScrollArea } from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import React, { JSX, useImperativeHandle } from "react"
import useSwr from "swr"
import { Sort, TablePagerState } from '.'
import { IFilter } from '../../models/Filter'
import { EmptyState } from "../ui/empty-state"

export * from "./row-action"

type Props = {
  name: string
  tbody: (data: any[]) => JSX.Element
  thead: JSX.Element
  filters?: { [key: string]: string | string[] | number | null | boolean | [string | number | Date | null, string | number | Date | null] }
  pagination?: boolean
  dataSource?: any[]
  loadData?: (filter?: IFilter) => Promise<any>
  limit?: number
  onResult?: any
  colCount?: number
  sorted?: [string, Sort]
  /** Replaces the default empty state (e.g. a ListState with a primary action). */
  empty?: JSX.Element
  /** Rendered instead of the rows when the request failed; `retry` reloads. */
  renderError?: (retry: () => void) => JSX.Element
  /** "fixed" makes the Th `w` widths binding, so a wide table fits its frame. */
  layout?: "auto" | "fixed"
  /** Horizontal scroll width for the table only, so a `footer` stays put while the rows scroll. */
  minWidth?: number
  /** Replaces the default pagination row; receives the pager state. */
  footer?: (pager: TablePagerState) => JSX.Element
}

export type ITableRef = { reload: () => void }

function CustomTable({ name, filters = {}, pagination = true, loadData, limit = 20, onResult, thead, tbody, colCount, sorted, empty, renderError, layout, minWidth, footer }: Props, ref: React.Ref<ITableRef>) {
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const tableRef = React.useRef<HTMLTableElement>(null)
  const [offset, setOffset] = React.useState({ page: 1, limit: limit || 20 })
  const [fallbackData, setFallbackData] = React.useState<{
    count: number
    rows: any[]
  }>({ count: 0, rows: [] })
  const [debouncedFilters] = useDebouncedValue(filters, 1000)
  const [debouncedSorted] = useDebouncedValue(sorted, 1000)

  // A new filter starts from page 1; staying on page 4 of a shorter result shows an empty table.
  const filterKey = JSON.stringify(debouncedFilters)
  React.useEffect(() => {
    setOffset((state) => (state.page === 1 ? state : { ...state, page: 1 }))
  }, [filterKey])

  const { data, mutate, isLoading, error } = useSwr(
    ['table', name, offset, debouncedFilters, debouncedSorted],
    async () => {
      if (loadData) {
        let res = await loadData({
          offset,
          filter: debouncedFilters,
          order: debouncedSorted
        } as IFilter)
        setFallbackData(res)
        return res
      }
      return { count: 0, rows: [] }
    },
    {
      fallbackData: fallbackData,
      onSuccess: (data) => {
        onResult && onResult(data)
        if (scrollRef.current) {
          scrollRef.current.scrollTo({ top: 0 })
        }
      },
    },
  )

  useImperativeHandle(ref, () => ({
    reload() {
      return mutate()
    },
  }))

  const table = (
    <BaseTable ref={tableRef} layout={layout} horizontalSpacing="md" verticalSpacing="xs" highlightOnHover striped="even" border={1}>
      {thead}
      {error && renderError ? (
        <BaseTable.Tbody>
          <BaseTable.Tr>
            <BaseTable.Td colSpan={colCount ? colCount : 0}>{renderError(() => mutate())}</BaseTable.Td>
          </BaseTable.Tr>
        </BaseTable.Tbody>
      ) : data?.rows?.length > 0 ? (
        tbody(data.rows)
      ) : (
        <BaseTable.Tbody>
          <BaseTable.Tr>
            <BaseTable.Td colSpan={colCount ? colCount : 0}>
              {empty ?? <EmptyState />}
            </BaseTable.Td>
          </BaseTable.Tr>
        </BaseTable.Tbody>
      )}
    </BaseTable>
  )

  const total = data?.count ?? 0
  const pageCount = Math.ceil(total / offset.limit)

  return (
    <Box>
      <ScrollArea h="100%" pos="relative">
        {minWidth ? <BaseTable.ScrollContainer minWidth={minWidth}>{table}</BaseTable.ScrollContainer> : table}
        <LoadingOverlay visible={isLoading} />
      </ScrollArea>

      {pagination && footer
        ? footer({
            page: offset.page,
            limit: offset.limit,
            total,
            setPage: (page) => setOffset((state) => ({ ...state, page })),
            setLimit: (next) => setOffset({ page: 1, limit: next }),
          })
        : pagination && pageCount > 1 && (
            <Box display="flex" my={15} style={{ flexDirection: "row", justifyContent: "flex-end" }}>
              <Pagination
                value={offset.page}
                onChange={(page) => setOffset((state) => ({ ...state, page: page }))}
                total={pageCount}
              />
            </Box>
          )}
    </Box>
  )
}

const CustomTableComponent = React.forwardRef(CustomTable)

export { CustomTableComponent as CustomTable }
